import React, { useState, useEffect, useRef } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';

/* ═══════════════════════════════════════════════════════════════════════════
   PROBLEMS SECTION  (v2 — mobile/tablet responsive)
   Three problems, dual-audience (technical + business buyers)
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Breakpoint hook ──────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1100 };
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

/* ── Stat pill ────────────────────────────────────────────────────────── */
function StatPill({ value, label, color }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', padding:'12px 16px', borderRadius:10, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', flex:1 }}>
      <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:26, fontWeight:800, color, lineHeight:1, marginBottom:4 }}>{value}</span>
      <span style={{ fontSize:11.5, color:'#9B90BA', lineHeight:1.4 }}>{label}</span>
    </div>
  );
}

/* ── Audience tag ─────────────────────────────────────────────────────── */
function AudienceTag({ label }) {
  return (
    <span style={{ fontSize:10, fontWeight:600, letterSpacing:1.2, textTransform:'uppercase', color:'#9B90BA', padding:'3px 9px', borderRadius:100, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', whiteSpace:'nowrap' }}>
      {label}
    </span>
  );
}

/* ── Problem card ─────────────────────────────────────────────────────── */
function ProblemCard({ problem, index, inView }) {
  const [hov, setHov] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position:'relative', overflow:'hidden',
        borderRadius:18, padding:'32px',
        background: hov ? problem.bgHov : 'rgba(255,255,255,0.025)',
        border: `1px solid ${hov ? problem.borderHov : 'rgba(255,255,255,0.07)'}`,
        transition:'all .35s ease',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: inView ? `${index * 0.12}s` : '0s',
        cursor:'default',
        display:'flex', flexDirection:'column', gap:0,
      }}
    >
      {/* Top colour stripe */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${problem.color},transparent)`, opacity:hov?1:0.4, transition:'opacity .35s' }}/>

      {/* Glow blob */}
      <div style={{ position:'absolute', top:-60, right:-60, width:180, height:180, borderRadius:'50%', background:`radial-gradient(circle,${problem.glow} 0%,transparent 70%)`, opacity:hov?1:0, transition:'opacity .4s ease', pointerEvents:'none' }}/>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:48, height:48, borderRadius:13, flexShrink:0, background:problem.bgHov, border:`1px solid ${problem.borderHov}`, display:'flex', alignItems:'center', justifyContent:'center', color:problem.color, transition:'all .3s' }}>
            {problem.icon}
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:problem.color, opacity:0.8, marginBottom:3 }}>Problem {String(index+1).padStart(2,'0')}</div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:22, fontWeight:800, color:'#EDE9F8', letterSpacing:'-0.5px', lineHeight:1.1 }}>{problem.title}</div>
          </div>
        </div>
      </div>

      {/* Audience tags */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:20 }}>
        {problem.audience.map((a,i) => <AudienceTag key={i} label={a}/>)}
      </div>

      {/* Problem statement */}
      <p style={{ fontSize:15.5, fontWeight:300, lineHeight:1.78, color:'#C8C0EC', marginBottom:24 }}>
        <span style={{ color:'#DDD8F2', fontWeight:500 }}>{problem.leadIn}{' '}</span>
        {problem.body}
      </p>

      {/* Stats row */}
      <div style={{ display:'flex', gap:10, marginBottom:24 }}>
        {problem.stats.map((s,i) => <StatPill key={i} value={s.value} label={s.label} color={problem.color}/>)}
      </div>

      {/* Divider */}
      <div style={{ height:1, background:`linear-gradient(90deg,${problem.borderHov},rgba(255,255,255,0.05))`, marginBottom:20, opacity:hov?1:0.4, transition:'opacity .35s' }}/>

      {/* RevUp solution */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, opacity:hov?1:0.55, transform:hov?'translateY(0)':'translateY(4px)', transition:'all .35s ease' }}>
        <div style={{ width:28, height:28, borderRadius:8, flexShrink:0, marginTop:1, background:problem.bgHov, border:`1px solid ${problem.borderHov}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={problem.color} strokeWidth="2.2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:problem.color, marginBottom:5, opacity:0.85 }}>RevUp removes this barrier</div>
          <div style={{ fontSize:13.5, color:'#C8C0EC', lineHeight:1.65, fontWeight:400 }}>{problem.solution}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Problems data ────────────────────────────────────────────────────── */
const PROBLEMS = [
  {
    title:'The Blind Spot',
    color:'#10B981', bgHov:'rgba(16,185,129,0.07)', borderHov:'rgba(16,185,129,0.28)', glow:'rgba(16,185,129,0.12)',
    audience:['CISO','CIO','Legal & Compliance'],
    leadIn:'67% of your employees are using AI you never approved.',
    body:"No audit trail. No access controls. No way to know which AI tools are touching your customer data, your intellectual property, or your regulated systems. You cannot govern what you cannot see — and right now, you cannot see most of it.",
    stats:[
      { value:'67%',  label:'of employees use\nunapproved AI tools' },
      { value:'48hr', label:'to full AI visibility\nwith RevSec' },
      { value:'150+', label:'GB AI traffic found\nin typical enterprise' },
    ],
    solution:'AI CISO discovers every AI agent and tool in your environment in 48 hours — including shadow deployments. Agent Discovery Engine scans GitHub, AWS, Azure, GCP, API gateways and SSO logs. You see everything. You govern everything.',
    icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/><line x1="3" y1="3" x2="21" y2="21" strokeWidth="1.8"/></svg>,
  },
  {
    title:'The Anchor',
    color:'#00D4AA', bgHov:'rgba(0,212,170,0.07)', borderHov:'rgba(0,212,170,0.28)', glow:'rgba(0,212,170,0.10)',
    audience:['CEO','COO','CTO'],
    leadIn:"Your competitors are shipping AI features. You're still maintaining apps from 1998.",
    body:"Every AI initiative you want to run — every agent, every intelligent workflow, every automation — requires your apps to be AI-agent ready first. PowerBuilder, Oracle Forms, VB6, Classic ASP: they cannot connect to AI. Your business logic is locked inside architecture that predates the AI era.",
    stats:[
      { value:'70%',  label:'of IT budget spent\nmaintaining legacy' },
      { value:'18mo', label:'average legacy\nmigration timeline' },
      { value:'6wks', label:'RevUp AI Architect\ndeployment target' },
    ],
    solution:'AI Architect modernises your legacy stack and makes every app AI-agent ready — not just rewritten, but re-architected with AI APIs, HITL queues, and pgvector from day one. Your AI initiatives unblock in weeks, not years.',
    icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 22V8M5 12H2a10 10 0 0 0 20 0h-3"/><path d="M12 8l-3 3m3-3l3 3"/></svg>,
  },
  {
    title:'The Compliance Trap',
    color:'#F59E0B', bgHov:'rgba(245,158,11,0.07)', borderHov:'rgba(245,158,11,0.28)', glow:'rgba(245,158,11,0.10)',
    audience:['CEO','CFO','General Counsel','Board'],
    leadIn:"Your board wants AI. Your legal team says not yet. Your competitors aren't waiting.",
    body:"EU AI Act. SOC2. HIPAA. Every time you want to move on AI, compliance becomes a 12-month project that kills the initiative before it starts. The result: your organisation sits frozen while the market moves. Speed and safety feel like a choice. They shouldn't be.",
    stats:[
      { value:'$670K', label:'average AI compliance\nrisk per enterprise' },
      { value:'Day 1', label:'EU AI Act compliant\nfrom first deployment' },
      { value:'97%',   label:'policy compliance\nrate with AI CISO' },
    ],
    solution:'AI CISO deploys AI Agent IAM, immutable audit trails and 11 enterprise policy templates — covering PII, HIPAA, SOC2 and EU AI Act — from day one. Compliance evidence is generated automatically, not assembled manually before an audit.',
    icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  },
];

/* ══ PROBLEMS SECTION ════════════════════════════════════════════════════ */
export default function Problems() {
  const { mob, tab } = useBreakpoint();
  const [headerRef, headerInView] = useInView(0.2);
  const [cardsRef,  cardsInView]  = useInView(0.1);

  return (
    <section style={{ position:'relative', padding:`${SECTION_PAD} 0`, overflow:'hidden' }}>

      {/* Atmosphere */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:200, background:'linear-gradient(to bottom, #09060F, transparent)' }}/>
        <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translateX(-50%)', width:800, height:400, background:'radial-gradient(ellipse,rgba(45,24,84,0.25) 0%,transparent 70%)', filter:'blur(60px)' }}/>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:200, background:'linear-gradient(to top, #09060F, transparent)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:2 }}>

        {/* Header */}
        <div ref={headerRef} style={{ maxWidth:680, marginBottom:64, opacity:headerInView?1:0, transform:headerInView?'translateY(0)':'translateY(20px)', transition:'opacity .7s ease, transform .7s ease' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px 5px 8px', borderRadius:100, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', marginBottom:24 }}>
            <div style={{ width:18, height:18, borderRadius:'50%', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:'#F59E0B' }}/>
            </div>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'#F59E0B' }}>Why Enterprises Come to RevUp</span>
          </div>

          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(36px,4.2vw,58px)', fontWeight:900, letterSpacing:'-1.5px', lineHeight:1.05, marginBottom:20, color:'#EDE9F8' }}>
            Three barriers standing between{' '}
            <span style={{ background:'linear-gradient(90deg,#00D4AA,#00B891)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              your enterprise and AI.
            </span>
          </h2>

          <p style={{ fontSize:17, fontWeight:300, lineHeight:1.78, color:'#9B90BA', maxWidth:580, margin:0 }}>
            Every enterprise we talk to is stuck on at least one of these.{' '}
            <span style={{ color:'#DDD8F2', fontWeight:400 }}>
              Most are stuck on all three. RevUp was built to remove them — then deploy the AI roles that accelerate everything after.
            </span>
          </p>
        </div>

        {/* Cards grid — 3-col desktop, 1-col tablet/mobile */}
        <div ref={cardsRef} style={{
          display:'grid',
          gridTemplateColumns: tab ? '1fr' : 'repeat(3,1fr)',
          gap: mob ? 14 : 20,
          maxWidth: tab ? 680 : 'none',
          margin: tab ? '0 auto' : '0',
        }}>
          {PROBLEMS.map((p,i) => (
            <ProblemCard key={i} problem={p} index={i} inView={cardsInView}/>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div style={{
          marginTop:56,
          display:'flex',
          flexDirection: mob ? 'column' : 'row',
          alignItems: mob ? 'stretch' : 'center',
          justifyContent:'space-between',
          flexWrap:'wrap', gap:20,
          padding: mob ? '24px' : '28px 36px',
          borderRadius:16,
          background:'rgba(0,212,170,0.04)',
          border:'1px solid rgba(0,212,170,0.12)',
          opacity: cardsInView?1:0,
          transform: cardsInView?'translateY(0)':'translateY(16px)',
          transition:'opacity .6s ease .5s, transform .6s ease .5s',
          textAlign: mob ? 'center' : 'left',
        }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:20, fontWeight:700, color:'#D4CEF0', marginBottom:6 }}>
              Which barrier is blocking your AI roadmap?
            </div>
            <div style={{ fontSize:14, color:'#9B90BA', lineHeight:1.6 }}>
              Tell us where you're stuck. We'll show you how fast we can unblock you.
            </div>
          </div>
          <button style={{
            background:'#00D4AA', color:'#09060F', border:'none', cursor:'pointer',
            padding:'13px 28px', borderRadius:9, fontSize:15, fontWeight:600,
            fontFamily:"'DM Sans',system-ui,sans-serif",
            boxShadow:'0 0 28px rgba(0,212,170,0.27)',
            transition:'all .25s ease', whiteSpace:'nowrap',
            width: mob ? '100%' : 'auto',
          }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 0 44px rgba(0,212,170,0.5)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 0 28px rgba(0,212,170,0.27)';}}
          >
            Book a Discovery Call →
          </button>
        </div>

      </div>
    </section>
  );
}